import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './Blogs.scss';

interface Blog {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
}

const Blogs: React.FC = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);

  // Sample blog data - in production this would come from an API
  const sampleBlogs: Blog[] = [
    {
      id: 1,
      slug: 'the-future-of-web-development-in-2025',
      title: 'The Future of Web Development in 2025',
      excerpt: 'Explore the latest trends and technologies shaping the future of web development.',
      category: 'Web Development',
      date: 'January 15, 2025',
      readTime: '5 min read',
      image: '/images/blog/web-dev-2025.jpg'
    },
    {
      id: 2,
      slug: 'react-19-whats-new-and-exciting',
      title: "React 19: What's New and Exciting",
      excerpt: 'Discover the new features and improvements in React 19.',
      category: 'React',
      date: 'January 10, 2025',
      readTime: '7 min read',
      image: '/images/blog/react-19.jpg'
    },
    {
      id: 3,
      slug: 'mobile-app-development-native-vs-cross-platform',
      title: 'Mobile App Development: Native vs Cross-Platform',
      excerpt: 'A comprehensive comparison to help you choose the right approach.',
      category: 'Mobile Development',
      date: 'January 5, 2025',
      readTime: '8 min read',
      image: '/images/blog/mobile-dev.jpg'
    },
    {
      id: 4,
      slug: 'ui-ux-trends-that-will-dominate-2025',
      title: 'UI/UX Trends That Will Dominate 2025',
      excerpt: 'Stay ahead of the curve with these emerging design trends.',
      category: 'UI/UX Design',
      date: 'December 28, 2024',
      readTime: '6 min read',
      image: '/images/blog/uiux-trends.jpg'
    },
    {
      id: 5,
      slug: 'building-scalable-node-js-applications',
      title: 'Building Scalable Node.js Applications',
      excerpt: 'Best practices for building enterprise-grade Node.js applications.',
      category: 'Backend Development',
      date: 'December 20, 2024',
      readTime: '10 min read',
      image: '/images/blog/nodejs.jpg'
    },
    {
      id: 6,
      slug: 'the-complete-guide-to-typescript-for-beginners',
      title: 'The Complete Guide to TypeScript for Beginners',
      excerpt: 'Everything you need to know to get started with TypeScript.',
      category: 'TypeScript',
      date: 'December 15, 2024',
      readTime: '12 min read',
      image: '/images/blog/typescript.jpg'
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setBlogs(sampleBlogs);
      setLoading(false);
    }, 500);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 100
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Blog | App Developer Pro - Web Development Insights</title>
        <meta name="description" content="Explore the latest insights, tutorials, and best practices in web development, mobile apps, UI/UX design, and more from App Developer Pro." />
        <meta name="keywords" content="web development blog, mobile app development, react tutorials, typescript guides, UI/UX design tips" />
        <link rel="canonical" href="https://appdeveloperpro.com/blogs" />
      </Helmet>

      <div className="blogs-page">
        {/* Hero Section */}
        <section className="blogs-hero">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="hero-content"
            >
              <h1>Our Blog</h1>
              <p>Insights, tutorials, and best practices in web development</p>
            </motion.div>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="blogs-grid-section">
          <div className="container">
            <div className="blogs-layout">
              {/* Main Content */}
              <div className="blogs-main-content">
                {loading ? (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading articles...</p>
                  </div>
                ) : (
                  <motion.div
                    className="blogs-grid"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {blogs.map((blog) => (
                      <motion.article
                        key={blog.id}
                        className="blog-card"
                        variants={itemVariants}
                      >
                        <Link to={`/blog/${blog.slug}`} className="blog-card-link">
                          <div className="blog-image">
                            <div className="image-placeholder">
                              <span>{blog.category}</span>
                            </div>
                          </div>
                          <div className="blog-content">
                            <div className="blog-meta">
                              <span className="category">{blog.category}</span>
                              <span className="date">{blog.date}</span>
                            </div>
                            <h2 className="blog-title">{blog.title}</h2>
                            <p className="blog-excerpt">{blog.excerpt}</p>
                            <div className="blog-footer">
                              <span className="read-time">{blog.readTime}</span>
                              <span className="read-more">Read More →</span>
                            </div>
                          </div>
                        </Link>
                      </motion.article>
                    ))}
                  </motion.div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="blogs-sidebar">
                {/* Search Widget */}
                <div className="sidebar-widget">
                  <h3 className="widget-title">
                    <span className="widget-icon">🔍</span> Search
                  </h3>
                  <div className="search-box">
                    <input type="text" placeholder="Search articles..." />
                    <button type="button">🔍</button>
                  </div>
                </div>

                {/* Recent Posts Widget */}
                <div className="sidebar-widget">
                  <h3 className="widget-title">
                    <span className="widget-icon">📰</span> Recent Posts
                  </h3>
                  <ul className="recent-posts-list">
                    {sampleBlogs.slice(0, 3).map((blog) => (
                      <li key={blog.id}>
                        <Link to={`/blog/${blog.slug}`}>
                          <h4>{blog.title}</h4>
                          <div className="post-meta">
                            <span className="post-date">📅 {blog.date}</span>
                            <span className="post-category">🏷️ {blog.category}</span>
                          </div>
                        </Link>
                        <p>{blog.excerpt.substring(0, 80)}...</p>
                        <Link to={`/blog/${blog.slug}`} className="read-more-link">
                          Read More →
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </div>

      <style>{`
        .blogs-page {
          min-height: 100vh;
        }

        .blogs-hero {
          background: linear-gradient(135deg, #0EA5E9 0%, #1E3A5F 100%);
          padding: 120px 0 80px;
          text-align: center;
          color: white;
        }

        .blogs-hero h1 {
          font-size: 3rem;
          margin-bottom: 1rem;
          font-weight: 700;
        }

        .blogs-hero p {
          font-size: 1.25rem;
          opacity: 0.9;
        }

        .blogs-grid-section {
          padding: 80px 0;
          background: #f8f9fa;
        }

        .container {
          max-width: 1440px;
          margin: 0 auto;
          padding: 0 40px;

          @media (max-width: 768px) {
            padding: 0 20px;
          }
        }

        .blogs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 30px;
        }

        .blog-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .blog-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }

        .blog-card-link {
          text-decoration: none;
          color: inherit;
          display: block;
        }

        .blog-image {
          height: 200px;
          overflow: hidden;
        }

        .image-placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #0EA5E9 0%, #1E3A5F 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .blog-content {
          padding: 24px;
        }

        .blog-meta {
          display: flex;
          gap: 15px;
          margin-bottom: 12px;
          font-size: 0.875rem;
        }

        .category {
          color: #0EA5E9;
          font-weight: 600;
        }

        .date {
          color: #666;
        }

        .blog-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1a1a2e;
          margin-bottom: 12px;
          line-height: 1.4;
        }

        .blog-excerpt {
          color: #666;
          line-height: 1.6;
          margin-bottom: 16px;
        }

        .blog-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 16px;
          border-top: 1px solid #eee;
        }

        .read-time {
          color: #888;
          font-size: 0.875rem;
        }

        .read-more {
          color: #0EA5E9;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .loading-state {
          text-align: center;
          padding: 60px 20px;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #0EA5E9;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .blogs-hero h1 {
            font-size: 2rem;
          }

          .blogs-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
};

export default Blogs;

